import React from 'react';
import withGATracking from 'enhancers/with-ga-tracking';
import toArray from 'utils/to-array';
import type { StyledElement, AriaAttributes, TestAutomation } from 'contracts';

import Html from '../html';

import { config, generateNumberRange } from './navigation-dots.settings';
import { calculateSliderPosition, isDotSmall, isDotTiny } from './utils';
import styles from './navigation-dots.module.scss';

interface NavigationDotsElement extends StyledElement, AriaAttributes, TestAutomation {
  total: number;
  activeIndex: number;
  theme?: 'light' | 'dark';
  onClick?: (newIndex: number) => void;
}

const width = config.getWidth();

const NavigationDots: React.FunctionComponent<NavigationDotsElement> = (props) => {
  const { testId, className, style, total, activeIndex, theme = 'light', onClick, ...rest } = props;

  const dotsRange = React.useMemo(() => {
    return generateNumberRange(0, total - 1);
  }, [total]);

  const handleClick = React.useCallback(
    (index: number) => {
      return (): void => {
        if (activeIndex === index) return;

        onClick?.(index);
      };
    },
    [activeIndex, onClick]
  );

  return (
    <Html.div
      testId={testId}
      className={[
        styles.navigationDots,
        theme === 'dark' ? styles.navigationDotsDark : styles.navigationDotsLight,
        'position-relative',
        'overflow-hidden',
        ...toArray(className),
      ]}
      style={{ width, ...style }}
      {...rest}
    >
      <Html.div
        className={[styles.dotsWrapper, 'position-absolute', 'd-flex', 'justify-content-center']}
        style={{
          left: calculateSliderPosition(activeIndex, total),
        }}
      >
        {dotsRange.map((index) => (
          <Html.button
            key={index}
            testId={`dot-${index}`}
            className={[
              'position-relative',
              'd-inline-block',
              'p-0',
              styles.dot,
              activeIndex === index && styles.dotActive,
              isDotSmall(index, activeIndex, total) && styles.dotSmall,
              isDotTiny(index, activeIndex, total) && styles.dotTiny,
            ]}
            aria-selected={activeIndex === index}
            onClick={handleClick(index)}
          />
        ))}
      </Html.div>
    </Html.div>
  );
};

export type { NavigationDotsElement };
export default withGATracking(NavigationDots, { action: 'click', handler: 'onClick' });
